/**
 * INTEGRATION REQUIREMENT COMPONENT
 * 
 * Componente reutilizável para seleção de integrações com filtros
 * Localizado em shared/ui para uso em múltiplos domínios
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';

import { X, Plug, Check, Plus } from "lucide-react";
import { cn } from '@/infrastructure/lib/utils';
import { IntegrationBaseSelector } from '@/features/Integrations/components/IntegrationBaseSelector';
import type { IntegrationBase } from '@/features/Integrations/types/integration';
import { getIntegrationDescription } from '@/shared/utils/i18nHelper';
import { IntegrationBaseService } from '@/features/Integrations/services/integrationsBaseService';
import { useAuth } from '@/features/Auth/state/AuthContext';

// Interfaces - usando IntegrationBase do domínio Integrations
interface Integration extends IntegrationBase {
  // Extende IntegrationBase com campos específicos se necessário
}

interface IntegrationFilter {
  type: 'classification' | 'name';
  value: string;
}

interface InstallerField {
  name: string;
  type: string;
  label?: string;
  description?: string;
  integrationType?: string;
  filters?: IntegrationFilter;
  required?: boolean;
}

interface IntegrationRequirementProps {
  field: InstallerField;
  selectedIntegrations: Integration[];
  onSelectionChange: (integrations: Integration[]) => void;
  className?: string;
}

export const IntegrationRequirement: React.FC<IntegrationRequirementProps> = ({
  field,
  selectedIntegrations,
  onSelectionChange,
  className
}) => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const hasAutoSelectedRef = useRef(false);

  // 🤖 Auto-seleção: Carregar automaticamente quando houver exatamente uma integração disponível
  useEffect(() => {
    const autoSelectIntegration = async () => {
      // Só executa se:
      // 1. Não há integrações selecionadas
      // 2. Há filtros configurados
      // 3. Não está em processo de auto-seleção (evitar loops)
      // 4. Ainda não tentou auto-selecionar (evita re-seleção após remoção manual)
      if (selectedIntegrations.length > 0 || !field.filters || isAutoSelecting || hasAutoSelectedRef.current) {
        return;
      }

      try {
        setIsAutoSelecting(true);

        const userOrganizationId = user?.organizationId;
        if (!userOrganizationId) {
          return;
        }

        // Buscar integrações base com os mesmos filtros usados no IntegrationBaseSelector
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const filterParams: any = {
          organization_id: userOrganizationId,
          status: 'active'
        };

        const allIntegrations = await IntegrationBaseService.getAll(filterParams);
        
        // Aplicar filtros específicos do field
        const filteredIntegrations = allIntegrations.filter((integration: IntegrationBase) => {
          switch (field.filters?.type) {
            case 'classification':
              return integration.classification === field.filters.value;
            case 'name':
              return integration.name === field.filters.value;
            default:
              return true;
          }
        });

        // ✅ Se houver EXATAMENTE UMA integração → auto-selecionar
        if (filteredIntegrations.length === 1) {
          const integration = filteredIntegrations[0];
          onSelectionChange([integration]);
        }
        // ❌ Se houver 0 ou mais de 1 → não faz nada (obriga seleção manual)
        
      } catch (error) {
        // Em caso de erro, falha silenciosamente e mantém comportamento de seleção manual
      } finally {
        setIsAutoSelecting(false);
        hasAutoSelectedRef.current = true; // Marca que já tentou auto-selecionar
      }
    };

    autoSelectIntegration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.filters, selectedIntegrations.length, user?.organizationId]);

  // 🎯 Obter iniciais do nome para avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // ❌ Remover integração selecionada
  const handleRemoveIntegration = (integrationId: string) => {
    const updatedIntegrations = selectedIntegrations.filter(
      integration => integration.id !== integrationId
    );
    onSelectionChange(updatedIntegrations);
  };

  // ➕ Abrir flyout para adicionar integração
  const handleAddIntegration = () => {
    setIsFlyoutOpen(true);
  };

  // 🎯 Callback quando uma integração é selecionada no flyout
  const handleIntegrationSelected = (integration: IntegrationBase) => {
    // Converter IntegrationBase para Integration se necessário
    const newIntegration: Integration = {
      ...integration,
      // Garantir que todos os campos obrigatórios estão presentes
    };
    
    // Verificar se já não foi selecionada
    if (!selectedIntegrations.find(i => i.id === newIntegration.id)) {
      onSelectionChange([...selectedIntegrations, newIntegration]);
    }
    
    setIsFlyoutOpen(false);
  };

  return (
    <div className={cn("space-y-1", className)}>
      {/* 🔌 Área de Integração Unificada */}
      <div>
        {selectedIntegrations.length > 0 ? (
          // Integração selecionada
          <div className="group flex items-center gap-4 p-4 bg-muted/30 hover:bg-muted/50 rounded-lg border border-border/50 transition-all duration-200">
            {/* Avatar Clean */}
            <div className="relative">
              {selectedIntegrations[0].logo_url ? (
                <img 
                  src={selectedIntegrations[0].logo_url}
                  alt={selectedIntegrations[0].name}
                  className="h-12 w-12 rounded-lg object-cover"
                />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary">
                    {getInitials(selectedIntegrations[0].name)}
                  </span>
                </div>
              )}
            </div>

            {/* Info Ultra Clean */}
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-foreground truncate">
                {selectedIntegrations[0].label || selectedIntegrations[0].name}
              </h4>
              {getIntegrationDescription(selectedIntegrations[0], i18n.language) && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {getIntegrationDescription(selectedIntegrations[0], i18n.language)}
                </p>
              )}
            </div>

            {/* Ícone de Check - Requisito Atendido */}
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                <Check className="h-4 w-4 text-white" />
              </div>
              
              {/* Botão Remover Discreto */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveIntegration(selectedIntegrations[0].id)}
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 transition-all duration-200"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ) : (
          // Área vazia clicável para adicionar integração
          <button
            onClick={handleAddIntegration}
            disabled={isFlyoutOpen}
            className={cn(
              "w-full h-16 flex items-center justify-between px-4 bg-muted/20 rounded-lg border-2 border-dashed border-muted-foreground/20",
              "hover:border-primary hover:bg-primary/5 hover:text-primary transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              isFlyoutOpen && "border-primary bg-primary/5 text-primary"
            )}
          >
            <div className="flex items-center gap-2">
              <Plug className="h-4 w-4" />
              <span className="text-sm font-medium">{field.label || field.name}</span>
            </div>
            
            {/* Ícone de Adicionar */}
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              {isFlyoutOpen ? (
                <div className="h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              ) : (
                <Plus className="h-4 w-4 text-primary" />
              )}
            </div>
          </button>
        )}
      </div>


      {/* 🚀 Flyout de Seleção de Integrações */}
      <IntegrationBaseSelector
        open={isFlyoutOpen}
        onClose={() => setIsFlyoutOpen(false)}
        onSelect={handleIntegrationSelected}
        selectedIntegrations={selectedIntegrations.map(i => i.id)}
        filters={field.filters}
        title={`Selecionar ${field.label || field.name}`}
        searchPlaceholder={`Buscar ${(field.label || field.name).toLowerCase()}...`}
      />
    </div>
  );
};
